import { Link } from "react-router-dom";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkDirective from "remark-directive";
import rehypeSlug from "rehype-slug";
import { remarkLegalHeadingIds } from "@scryerlabs/legal";
import { remarkLegalCallouts } from "./remark-legal-callouts";
import { getLegalPathSlug } from "./legal-content";

/**
 * Renders the body of one document from @scryerlabs/legal.
 *
 * Shared by the standalone legal pages and the acceptance dialog so both show the
 * same text through the same plugins. The two differ only in what a heading and a
 * cross-document link should do, which is what the props below decide.
 *
 * Raw HTML in a document is NOT rendered — react-markdown ignores it by default,
 * and enabling it would need rehype-raw plus a sanitiser. That matters more here
 * than in a help article: this content is a published legal agreement, and an
 * unsanitised passthrough on the text a customer accepts before paying is not a
 * trade worth making for formatting.
 */
const REMARK_PLUGINS = [
  remarkGfm,
  remarkDirective,
  remarkLegalCallouts,
  // Before rehype-slug, so an explicit {#anchor} from the document wins and
  // rehype-slug only fills in for headings that have none. The anchors have to
  // match the brochure's, because both are linked from outside our own code.
  remarkLegalHeadingIds,
];
const REHYPE_PLUGINS = [rehypeSlug];

interface LegalDocumentBodyProps {
  /** The Markdown, frontmatter already removed. */
  body: string;
  /**
   * Whether each heading carries a `#` permalink. On a page it is how a clause is
   * cited; in a dialog there is no address to link to, so it is left off.
   */
  headingAnchors?: boolean;
  /**
   * What a link to another legal document should do. Given the target slug —
   * "terms", "privacy" or "disclosures". When omitted such links route normally,
   * which is right on a page and wrong inside a blocking dialog, where the router
   * cannot take the customer anywhere.
   */
  onNavigateToDocument?: (slug: string) => void;
}

function headingAnchor(id: string | undefined) {
  if (!id) return null;
  return (
    <a
      href={`#${id}`}
      className="help-anchor text-decoration-none"
      aria-label="Link to this section"
    >
      #
    </a>
  );
}

function isInternal(href: string): boolean {
  return href.startsWith("/") && !href.startsWith("//");
}

export function LegalDocumentBody({
  body,
  headingAnchors = true,
  onNavigateToDocument,
}: LegalDocumentBodyProps) {
  const components = {
    // data-toc-label rides through to the DOM so the contents rail can prefer it
    // over the full heading text.
    h2: ({ id, children, ...rest }: React.ComponentProps<"h2">) => (
      <h2 id={id} data-toc-label={(rest as Record<string, string>)["data-toc-label"]}>
        {children}
        {headingAnchors && headingAnchor(id)}
      </h2>
    ),

    h3: ({ id, children }: React.ComponentProps<"h3">) => (
      <h3 id={id}>
        {children}
        {headingAnchors && headingAnchor(id)}
      </h3>
    ),

    a: ({ href, children, ...rest }: React.ComponentProps<"a">) => {
      if (!href) return <span>{children}</span>;

      if (isInternal(href)) {
        const targetSlug = getLegalPathSlug(href);

        // The documents cross-reference each other. Inside the acceptance dialog
        // all three are already present, so the link opens the sibling panel —
        // routing there would be a link that visibly does nothing, since the gate
        // holds the app on this dialog either way.
        if (targetSlug && onNavigateToDocument) {
          return (
            <button
              type="button"
              className="btn btn-link p-0 align-baseline border-0"
              onClick={() => onNavigateToDocument(targetSlug)}
            >
              {children}
            </button>
          );
        }

        // Internal links route client-side; a full page load would drop the MSAL
        // session state and flash the whole shell.
        return <Link to={href}>{children}</Link>;
      }

      return (
        <a {...rest} href={href} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      );
    },

    table: ({ children }: React.ComponentProps<"table">) => (
      <div className="table-responsive mb-3">
        <table className="table table-sm align-middle mb-0">{children}</table>
      </div>
    ),
  };

  return (
    <Markdown
      remarkPlugins={REMARK_PLUGINS}
      rehypePlugins={REHYPE_PLUGINS}
      components={components}
    >
      {body}
    </Markdown>
  );
}
